import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAdminEntity from '../../../hooks/useAdminEntity';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { getInvestorProjectDetailEndpoint } from '../services/investorService';
import {
  getCharacteristicsMasterPlanEndpoint,
  getExpensesMasterPlanEndpoint,
  getMasterPlanProjectEndpoint,
  createMasterPlanProjectEndpoint,
  updateMasterPlanProjectEndpoint,
  uploadMasterPlanCoverEndpoint,
  uploadMasterPlanBlueprintsEndpoint,
  getMasterPlanIndicatorsEndpoint,
  updateMasterPlanIndicatorsEndpoint,
  deleteMasterPlanBlueprintEndpoint,
  deleteAllMasterPlanBlueprintsEndpoint,
  updateOperablePlanEndpoint,
} from '../services/masterPlanService';
import {
  ICharacteristic,
  IOperablePlan,
  MasterPlanCharacteristic,
  MasterPlanExpense,
  RetrieveMasterPlan,
  MasterPlanIndicator,
} from '../models/masterPlanModel';
import { InvestorProjectDetail } from '../models/investors';

interface Props {
  projectId: string;
}

const useMasterPlan = ({ projectId }: Props) => {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { callEndpoint } = useCallApiAndLoad();
  const navigate = useNavigate();

  const [sweetAlert, setSweetAlert] = useState(false);
  const [alertProps, setAlertProps] = useState<any>({});
  // undefined = still loading, null = loaded but doesn't exist
  const [masterPlan, setMasterPlan] = useState<
    RetrieveMasterPlan | null | undefined
  >(undefined);
  const [masterPlanLoaded, setMasterPlanLoaded] = useState(false);

  // Indicators
  const [selectedKindBuyer, setSelectedKindBuyer] = useState<string[]>([]);
  const [selectedFeatured, setSelectedFeatured] = useState<string[]>([]);

  // Characteristics rows being edited
  const [rows, setRows] = useState<ICharacteristic[]>([]);
  const [pendingKey, setPendingKey] = useState('');
  const [pendingValue, setPendingValue] = useState('');

  // Operable plan
  const [operablePlan, setOperablePlan] = useState<IOperablePlan>({
    airbnb_occupancy_rate: 0,
    airbnb_price_per_night: 0,
    currency: '',
    expenses: [],
    long_term_rent: [],
  });

  const showError = useCallback((text: string) => {
    setAlertProps({
      warning: true,
      title: 'Error',
      text,
      onConfirm: () => setSweetAlert(false),
    });
    setSweetAlert(true);
  }, []);

  const showSuccess = useCallback(() => {
    setAlertProps({
      success: true,
      title: 'Guardado',
      text: 'Los datos han sido guardados correctamente.',
      onConfirm: () => setSweetAlert(false),
    });
    setSweetAlert(true);
  }, []);

  const getProject = useCallback(
    () => getInvestorProjectDetailEndpoint(projectId, token),
    [projectId, token],
  );
  const [project] = useAdminEntity<InvestorProjectDetail>(
    getProject,
    showError,
  );

  const getCatalog = useCallback(
    () => getCharacteristicsMasterPlanEndpoint(token),
    [token],
  );
  const [catalog] = useAdminEntity<MasterPlanCharacteristic[]>(
    getCatalog,
    showError,
  );

  const getExpensesCatalog = useCallback(
    () => getExpensesMasterPlanEndpoint(token),
    [token],
  );
  const [expensesCatalog] = useAdminEntity<MasterPlanExpense[]>(
    getExpensesCatalog,
    showError,
  );

  // Kind Buyer indicators catalog
  const getKindBuyerCatalog = useCallback(
    () => getMasterPlanIndicatorsEndpoint(token, 'kind_buyer'),
    [token],
  );
  const [kindBuyerCatalog, , loadingKindBuyer] = useAdminEntity<MasterPlanIndicator[]>(
    getKindBuyerCatalog,
    showError,
  );
  const kindBuyerOptions = ((kindBuyerCatalog as MasterPlanIndicator[]) || []).map((i: MasterPlanIndicator) => ({
    value: i.key,
    label: i.description ? `${i.header} (${i.description})` : i.header,
  }));

  // Featured indicators catalog
  const getFeaturedCatalog = useCallback(
    () => getMasterPlanIndicatorsEndpoint(token, 'featured'),
    [token],
  );
  const [featuredCatalog, , loadingFeatured] = useAdminEntity<MasterPlanIndicator[]>(
    getFeaturedCatalog,
    showError,
  );
  const featuredOptions = ((featuredCatalog as MasterPlanIndicator[]) || []).map((i: MasterPlanIndicator) => ({
    value: i.key,
    label: i.description ? `${i.header} (${i.description})` : i.header,
  }));

  // Load master plan after catalog arrives (404 = not found, not an error)
  useEffect(() => {
    if (!catalog) return;
    const load = async () => {
      const { status, data } = await callEndpoint(
        getMasterPlanProjectEndpoint(projectId, token),
      );
      let found: RetrieveMasterPlan | null = null;
      if (status === 200) {
        const response = fetchResponseAdapter(data);
        found = response.data as RetrieveMasterPlan;
      }
      setMasterPlan(found);
      setMasterPlanLoaded(true);
      setRows(found?.characteristics ?? []);
      setOperablePlan({
        airbnb_occupancy_rate: (found?.operable_plan?.airbnb_occupancy_rate ?? 0) * 100,
        airbnb_price_per_night: found?.operable_plan?.airbnb_price_per_night ?? 0,
        currency: found?.operable_plan?.currency ?? '',
        expenses: (found?.operable_plan?.expenses ?? []).map((e) =>
          e.key === 'airbnb_fee_pct' ? { ...e, value: e.value * 100 } : e,
        ),
        long_term_rent: found?.operable_plan?.long_term_rent ?? [],
      });
      // Split saved indicators into their respective dropdowns
      const savedIndicators = found?.indicators ?? [];
      setSelectedKindBuyer(savedIndicators.filter((k: string) => k.startsWith('kind_buyer')));
      setSelectedFeatured(savedIndicators.filter((k: string) => k.startsWith('featured')));
    };
    load();
  }, [catalog, projectId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const isNew = masterPlan === null;

  const availableCatalog: MasterPlanCharacteristic[] = (catalog || []).filter(
    (c) => !rows.some((r) => r.key === c.key),
  );

  const addRow = useCallback(() => {
    if (!pendingKey) return;
    setRows((prev) => [...prev, { key: pendingKey, value: pendingValue }]);
    setPendingKey('');
    setPendingValue('');
  }, [pendingKey, pendingValue]);

  const removeRow = useCallback((key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const updateValue = useCallback((key: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, value } : r)));
  }, []);

  const updateExpenseValue = useCallback((key: string, value: number) => {
    setOperablePlan((prev) => ({
      ...prev,
      expenses: prev.expenses.map((r) => (r.key === key ? { ...r, value } : r)),
    }));
  }, []);

  const updateLongTermRentValue = useCallback(
    (modelId: string, monthly_rent: number) => {
      setOperablePlan((prev) => ({
        ...prev,
        long_term_rent: prev.long_term_rent.map((r) =>
          r.model_id === modelId ? { ...r, monthly_rent } : r,
        ),
      }));
    },
    [],
  );

  const onSaveOperablePlan = async () => {
    if (!masterPlan) return;
    const payload: IOperablePlan = {
      ...operablePlan,
      airbnb_occupancy_rate: operablePlan.airbnb_occupancy_rate / 100,
      expenses: operablePlan.expenses.map((e) =>
        e.key === 'airbnb_fee_pct' ? { ...e, value: e.value / 100 } : e,
      ),
    };
    const { status } = await callEndpoint(
      updateOperablePlanEndpoint(projectId, masterPlan.id, payload, token),
    );
    if (status === 200) {
      showSuccess();
    }
  };

  const onSubmit = async () => {
    const payload = {
      characteristics: rows.map(({ key, value }) => ({ key, value })),
    };

    if (isNew) {
      const { status, data } = await callEndpoint(
        createMasterPlanProjectEndpoint(projectId, payload, token),
      );
      if (status === 200 || status === 201) {
        const response = fetchResponseAdapter(data);
        setMasterPlan(response.data as RetrieveMasterPlan);
        showSuccess();
      }
    } else if (masterPlan) {
      const { status } = await callEndpoint(
        updateMasterPlanProjectEndpoint(
          projectId,
          masterPlan.id,
          payload,
          token,
        ),
      );
      if (status === 200) {
        showSuccess();
      }
    }
  };

  const onSaveIndicators = async () => {
    if (!masterPlan) return;
    const combined = [...selectedKindBuyer, ...selectedFeatured];
    const payload = { indicators: combined };
    const { status } = await callEndpoint(
      updateMasterPlanIndicatorsEndpoint(projectId, masterPlan.id, payload, token),
    );
    if (status === 200) {
      showSuccess();
    }
  };

  const handleUploadCover = useCallback(
    async (files: Array<any>) => {
      if (!masterPlan || files.length === 0) return;
      const formData = new FormData();
      formData.append('cover', files[0]);
      const { status } = await callEndpoint(
        uploadMasterPlanCoverEndpoint(projectId, masterPlan.id, formData, token),
      );
      if (status === 200) navigate(0);
    },
    [masterPlan, projectId, callEndpoint, navigate, token],
  );

  const handleUploadBlueprints = useCallback(
    async (entries: Array<{ view: string; file: File }>) => {
      if (!masterPlan || entries.length === 0) return;
      const formData = new FormData();
      entries.forEach(({ view, file }) => formData.append(view, file));
      const { status } = await callEndpoint(
        uploadMasterPlanBlueprintsEndpoint(projectId, masterPlan.id, formData, token),
      );
      if (status === 200) navigate(0);
    },
    [masterPlan, projectId, callEndpoint, navigate, token],
  );

  const onDeleteBlueprint = useCallback(
    async (item: { view: string; image?: string | null }) => {
      if (!masterPlan || !item.image) return;
      const { status } = await callEndpoint(
        deleteMasterPlanBlueprintEndpoint(
          projectId,
          masterPlan.id,
          { path: item.image },
          token,
        ),
      );
      if (status === 200) navigate(0);
    },
    [masterPlan, projectId, callEndpoint, navigate, token],
  );

  const onDeleteAllBlueprints = useCallback(async () => {
    if (!masterPlan) return;
    const { status } = await callEndpoint(
      deleteAllMasterPlanBlueprintsEndpoint(projectId, masterPlan.id, token),
    );
    if (status === 200) navigate(0);
  }, [masterPlan, projectId, callEndpoint, navigate, token]);

  const isLoading = !project || !catalog || !masterPlanLoaded;

  return {
    project,
    catalog,
    expensesCatalog,
    operablePlan,
    setOperablePlan,
    updateExpenseValue,
    updateLongTermRentValue,
    onSaveOperablePlan,
    masterPlan,
    isNew,
    isLoading,
    rows,
    availableCatalog,
    pendingKey,
    setPendingKey,
    pendingValue,
    setPendingValue,
    addRow,
    removeRow,
    updateValue,
    onSubmit,
    handleUploadCover,
    handleUploadBlueprints,
    sweetAlert,
    alertProps,
    onDeleteBlueprint,
    onDeleteAllBlueprints,
    // Indicators
    kindBuyerOptions,
    featuredOptions,
    loadingKindBuyer,
    loadingFeatured,
    selectedKindBuyer,
    setSelectedKindBuyer,
    selectedFeatured,
    setSelectedFeatured,
    onSaveIndicators,
  };
};

export default useMasterPlan;
